const validPlacements = new Set(["block-start", "block-end", "inline-start", "inline-end"]);
const oppositeSide = {
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left"
};

const normalizePlacement = (placement) => (
    validPlacements.has(placement) ? placement : "block-start"
);

const placementToSide = (placement, direction) => {
    if (placement === "block-end") {
        return "bottom";
    }

    if (placement === "inline-start") {
        return direction === "rtl" ? "right" : "left";
    }

    if (placement === "inline-end") {
        return direction === "rtl" ? "left" : "right";
    }

    return "top";
};

const sideToPlacement = (side, direction) => {
    if (side === "bottom") {
        return "block-end";
    }

    if (side === "left") {
        return direction === "rtl" ? "inline-end" : "inline-start";
    }

    if (side === "right") {
        return direction === "rtl" ? "inline-start" : "inline-end";
    }

    return "block-start";
};

const positionForSide = (side, anchor, surface, gap) => {
    const anchorCenterX = anchor.left + (anchor.width / 2);
    const anchorCenterY = anchor.top + (anchor.height / 2);

    if (side === "bottom") {
        return {
            left: anchorCenterX - (surface.width / 2),
            top: anchor.bottom + gap
        };
    }

    if (side === "left") {
        return {
            left: anchor.left - surface.width - gap,
            top: anchorCenterY - (surface.height / 2)
        };
    }

    if (side === "right") {
        return {
            left: anchor.right + gap,
            top: anchorCenterY - (surface.height / 2)
        };
    }

    return {
        left: anchorCenterX - (surface.width / 2),
        top: anchor.top - surface.height - gap
    };
};

const overflowScore = (position, surface, viewport, padding) => {
    const viewportRight = viewport.left + viewport.width - padding;
    const viewportBottom = viewport.top + viewport.height - padding;

    return (
        Math.max((viewport.left + padding) - position.left, 0) +
        Math.max((position.left + surface.width) - viewportRight, 0) +
        Math.max((viewport.top + padding) - position.top, 0) +
        Math.max((position.top + surface.height) - viewportBottom, 0)
    );
};

const clamp = (value, minimum, maximum) => (
    Math.min(Math.max(value, minimum), Math.max(minimum, maximum))
);

export const computeRcdFloatingPosition = ({
    anchorRect,
    surfaceRect,
    placement = "block-start",
    direction = "ltr",
    viewport = { left: 0, top: 0, width: 0, height: 0 },
    padding = 12,
    gap = 8
}) => {
    const preferredSide = placementToSide(normalizePlacement(placement), direction);
    const candidates = [
        preferredSide,
        oppositeSide[preferredSide],
        "top",
        "bottom",
        "left",
        "right"
    ].filter((side, index, values) => values.indexOf(side) === index);
    const minimumLeft = viewport.left + padding;
    const maximumLeft = viewport.left + viewport.width - surfaceRect.width - padding;
    const minimumTop = viewport.top + padding;
    const maximumTop = viewport.top + viewport.height - surfaceRect.height - padding;

    const scored = candidates.map((side) => {
        const rawPosition = positionForSide(side, anchorRect, surfaceRect, gap);
        const position = {
            left: side === "top" || side === "bottom"
                ? clamp(rawPosition.left, minimumLeft, maximumLeft)
                : rawPosition.left,
            top: side === "left" || side === "right"
                ? clamp(rawPosition.top, minimumTop, maximumTop)
                : rawPosition.top
        };
        return {
            side,
            position,
            score: overflowScore(position, surfaceRect, viewport, padding)
        };
    });

    const best = scored.reduce((current, candidate) => (
        candidate.score < current.score ? candidate : current
    ));
    const left = clamp(best.position.left, minimumLeft, maximumLeft);
    const top = clamp(best.position.top, minimumTop, maximumTop);
    const arrowPadding = Math.max(gap, 8);

    return {
        side: best.side,
        placement: sideToPlacement(best.side, direction),
        left,
        top,
        arrowLeft: clamp(
            anchorRect.left + (anchorRect.width / 2) - left,
            arrowPadding,
            surfaceRect.width - arrowPadding
        ),
        arrowTop: clamp(
            anchorRect.top + (anchorRect.height / 2) - top,
            arrowPadding,
            surfaceRect.height - arrowPadding
        )
    };
};

export const getRcdVisualViewport = () => {
    const visualViewport = window.visualViewport;
    return {
        left: visualViewport?.offsetLeft || 0,
        top: visualViewport?.offsetTop || 0,
        width: visualViewport?.width || window.innerWidth,
        height: visualViewport?.height || window.innerHeight
    };
};

export const parseRcdCssLength = (value, fallback) => {
    const normalized = String(value || "").trim();
    const numeric = Number.parseFloat(normalized);
    if (!Number.isFinite(numeric)) {
        return fallback;
    }

    if (normalized.endsWith("rem")) {
        return numeric * Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
    }

    if (normalized.endsWith("em")) {
        return numeric * Number.parseFloat(getComputedStyle(document.body).fontSize);
    }

    return numeric;
};
